import { Component, ElementRef, Injectable, ViewChild, inject } from "@angular/core";
import { useTranslationNamespace } from "@logex/framework/lg-localization";
import { getDialogFactoryBase, IDialogComponent, LgDialogFactory } from "@logex/framework/ui-core";
import {
    DialogStateEnum,
    FileUploader,
    IPasteButtonColumnInfo,
    IPasteResult,
    IPreprocessDataCallback,
    LgPasteDialogComponent,
    PasteDialogConfiguration,
    UnmatchedHandlingType
} from "@logex/framework/ui-toolbox";
import { map, Observable } from "rxjs";
import { read, utils, WorkBook, WorkSheet } from "xlsx";

export interface ImportDialogStats {
    numOfPastedRows: number;
    numOfErrorRows: number;
}

@Component({
    selector: "app-import-dialog",
    templateUrl: "./import-dialog.component.html",
    styleUrls: ["./import-dialog.component.scss"],
    providers: [useTranslationNamespace("APP._Dialogs.ImportDialog")],
    standalone: false
})
export class ImportDialogComponent
    extends LgPasteDialogComponent
    implements IDialogComponent<ImportDialogComponent>
{
    @ViewChild("fileInput") _fileInput!: ElementRef<HTMLInputElement>;
    _uploader: FileUploader = new FileUploader({ url: "" });
    _dragging = false;
    _downloadTemplateCallback?: () => void;

    showWithStats(
        columns: IPasteButtonColumnInfo[],
        originalData: any[],
        title: string,
        preprocessData: IPreprocessDataCallback,
        unmatched: UnmatchedHandlingType,
        extraInformation: string,
        configuration?: PasteDialogConfiguration,
        downloadTemplateCallback?: () => void
    ): Observable<[IPasteResult, ImportDialogStats]> {
        this._downloadTemplateCallback = downloadTemplateCallback;
        return super
            .show(
                columns,
                originalData,
                title,
                preprocessData,
                unmatched,
                extraInformation,
                configuration
            )
            .pipe(
                map(result => [
                    result,
                    {
                        numOfPastedRows: this._data.length,
                        numOfErrorRows: this._data.filter(row => !this._isValidRow(row)).length
                    }
                ])
            );
    }

    _onFileDrop(files: File[] | FileList): void {
        const reader: FileReader = new FileReader();
        reader.onload = (e: any) => {
            const bstr: string = e.target.result;
            const workBook: WorkBook = read(bstr, { type: "binary" });
            const wsname: string = workBook.SheetNames[0];
            const ws: WorkSheet = workBook.Sheets[wsname];

            const parsed: any[][] = utils.sheet_to_json(ws, {
                defval: null,
                range: this._getDataRange(ws),
                header: 1,
                blankrows: false
            });

            this._state = DialogStateEnum.Pasted;
            this._pastedData = parsed;
            this._tryToMatchHeaders();
            this._makePreview();
        };
        reader.readAsBinaryString(files[0]);
        this._fileInput.nativeElement.value = "";
    }

    _fileSelected(e: Event): void {
        const target = e.target as HTMLInputElement;
        const files: FileList = target.files!;
        this._onFileDrop(files);
    }

    _draggingOverInput(dragging: boolean): void {
        this._dragging = dragging;
    }

    private _getDataRange(ws: WorkSheet): string {
        const dataColOfset = 1;
        const dataRowOfset = 2;
        const colsLength = this._columns.length;
        const rowsLength = utils.sheet_to_json(ws, {
            range: dataRowOfset,
            header: 1,
            blankrows: false
        }).length;

        const firstCol = utils.encode_col(dataColOfset);
        const firstRow = utils.encode_row(dataRowOfset);
        const lastCol = utils.encode_col(dataColOfset + colsLength - 1);
        const lastRow = utils.encode_row(dataRowOfset + rowsLength - 1);

        return `${firstCol}${firstRow}:${lastCol}${lastRow}`;
    }
}

@Injectable()
export class ImportDialog extends getDialogFactoryBase(
    ImportDialogComponent,
    "show",
    "showWithStats"
) {
    constructor() {
        super(inject(LgDialogFactory));
    }
}
